/**
 * PinnedListRow — single row in the borrower Watchlist "Pinned" panel.
 *
 * Layout: 32×32 kind icon + title (13px w500) + sub (11.5px dim) +
 * "Watching since {date}" GlassChip (mint dot) + bell IconButton + moreV
 * IconButton. The whole row navigates via the title; the action buttons live
 * inside the row but get `stopPropagation` so they fire independently.
 *
 * Icon background uses each pin kind's id-bg color from `utils.ts`
 * (`ENTITY_BG` / `ENTITY_ACCENT`) so loan / application / case rows stay
 * visually grouped with their chips elsewhere in the portal.
 */

'use client';

import FolderIcon from '@mui/icons-material/Folder';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import {
  Box,
  baseColors,
  GlassChip,
  GlassIconButton,
  GlassTooltip,
  iconSizes,
  Stack,
  type SvgIconProps,
  Typography,
  useI18n,
} from '@ops-matrix/design-system-ui';
import type { ComponentType, MouseEvent, ReactElement } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { ENTITY_ACCENT, ENTITY_BG, ENTITY_DEEP_LINK, type HomePin } from './utils';

export interface PinnedListRowProps {
  pin: HomePin;
  onRemove?: (pinId: string) => void;
  onToggleNotifications?: (pinId: string) => void;
}

/**
 * Kind → icon mapping. Loan/application/case use FolderIcon (default kind icon
 * for a record bundle); rate-watch pins render with TrendingUpIcon. Other pin
 * kinds inherit FolderIcon as a safe fallback.
 *
 * `'rate'` was added to HomePinEntityType in shared-types/home.dto.ts so the
 * borrower can pin a rate threshold (e.g. "30y < 6.5%"). The notification
 * trigger that fires when the threshold crosses lives in
 * 27-notification-service; the pin row itself is wired here.
 */
const KIND_ICON: Record<string, ComponentType<SvgIconProps>> = {
  case: FolderIcon,
  loan: FolderIcon,
  application: FolderIcon,
  partner: FolderIcon,
  contact: FolderIcon,
  consumer: FolderIcon,
  rate: TrendingUpIcon,
};

function formatWatchingSince(createdAt: HomePin['createdAt']): string {
  // tRPC serialises Date → string on the wire; tolerate both.
  const date = createdAt instanceof Date ? createdAt : new Date(createdAt as unknown as string);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export function PinnedListRow({ pin, onRemove, onToggleNotifications }: PinnedListRowProps): ReactElement {
  const { t } = useI18n();
  const accent = ENTITY_ACCENT[pin.entityType];
  const bg = ENTITY_BG[pin.entityType];
  const KindIcon = KIND_ICON[pin.entityType] ?? FolderIcon;
  const sub = t(`home.watchlist.entityType.${pin.entityType}`);
  const watchingSince = formatWatchingSince(pin.createdAt);

  const stop = (e: MouseEvent) => e.stopPropagation();

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        minHeight: 56,
        px: 1.5,
        py: 1,
        borderRadius: 1.5,
        transition: 'background-color 120ms ease',
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      {/* Kind icon (32×32) */}
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: bg,
          color: accent,
          flexShrink: 0,
        }}
      >
        <KindIcon sx={{ fontSize: iconSizes.md }} />
      </Box>

      {/* Title + sub (whole title block is the navigation link) */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box
          component={RouterLink}
          to={ENTITY_DEEP_LINK[pin.entityType](pin.entityId)}
          sx={{
            color: 'text.primary',
            textDecoration: 'none',
            display: 'block',
            transition: 'color 120ms ease',
            '&:hover': { color: 'primary.main' },
          }}
        >
          <Typography variant="body2" noWrap sx={{ fontWeight: 500, lineHeight: 1.3 }}>
            {pin.label}
          </Typography>
          <Typography variant="caption" noWrap sx={{ color: 'text.secondary', display: 'block', mt: 0.25 }}>
            {sub}
          </Typography>
        </Box>
      </Box>

      {/* Watching since chip */}
      {watchingSince && (
        <GlassChip
          label={t('consumer.watchlist.watchingSince', { date: watchingSince })}
          size="small"
          color="success"
          variant="outlined"
          sx={{
            '& .MuiChip-label': { fontSize: '0.75rem' },
            borderColor: baseColors.teal[400],
          }}
        />
      )}

      {/* Notifications toggle
          R8: 44×44 hit area per portals.md (Apple HIG / MD3 floor). These
          aren't in-row exceptions — the bell IS the primary notification
          affordance on this row (no other entry point), and MoreVert is the
          only remove action. Both need the full touch target. */}
      <Stack direction="row" spacing={0.5} onClick={stop}>
        <GlassTooltip title={t('common.notifications')}>
          <GlassIconButton
            size="medium"
            aria-label={t('common.notifications')}
            onClick={() => onToggleNotifications?.(pin.id)}
            sx={{ width: 44, height: 44 }}
          >
            <NotificationsActiveIcon sx={{ fontSize: iconSizes.base }} />
          </GlassIconButton>
        </GlassTooltip>

        {/* MoreVert acts as remove for now until the suggestion menu lands. */}
        <GlassTooltip title={t('home.watchlist.remove')}>
          <GlassIconButton
            size="medium"
            aria-label={t('home.watchlist.remove')}
            onClick={() => onRemove?.(pin.id)}
            sx={{ width: 44, height: 44 }}
          >
            <MoreVertIcon sx={{ fontSize: iconSizes.base }} />
          </GlassIconButton>
        </GlassTooltip>
      </Stack>
    </Box>
  );
}
